import { useId } from "react";

/**
 * Locked-content illustration for both lock screens (course-level overlay and
 * the in-player lesson gate).
 *
 * Drawn inline rather than shipped as a raster asset: it recolours itself from
 * the membership's brand colour, stays sharp on any display, and adds nothing
 * to the bundle's network cost. The legacy member app used a flat PNG padlock
 * with shield/alert badges — this keeps that read (padlock, reassurance badge)
 * in a current, dimensional style.
 *
 * `accent` drives everything, so a drip/coming-soon lock can pass amber while an
 * upgrade lock passes the brand colour.
 */
export default function LockIllustration({ accent, className = "" }) {
    // Gradient ids must be unique per instance — two mounted copies sharing an
    // id makes the second one render with the first one's fill.
    const uid = useId().replace(/:/g, "");
    const g = (name) => `${name}-${uid}`;

    return (
        <svg
            viewBox="0 0 240 200"
            className={className}
            fill="none"
            role="img"
            aria-label="Locked content"
        >
            <defs>
                <linearGradient id={g("body")} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={accent} />
                    <stop offset="100%" stopColor={accent} stopOpacity="0.72" />
                </linearGradient>
                <linearGradient id={g("shackle")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#cbd5e1" />
                    <stop offset="45%" stopColor="#f1f5f9" />
                    <stop offset="100%" stopColor="#94a3b8" />
                </linearGradient>
                <linearGradient id={g("card")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="100%" stopColor="#eef2f7" />
                </linearGradient>
                <radialGradient id={g("aura")} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={accent} stopOpacity="0.30" />
                    <stop offset="70%" stopColor={accent} stopOpacity="0.06" />
                    <stop offset="100%" stopColor={accent} stopOpacity="0" />
                </radialGradient>
            </defs>

            {/* brand aura */}
            <circle cx="120" cy="100" r="88" fill={`url(#${g("aura")})`} />
            {/* dotted orbit — depth without noise */}
            <circle
                cx="120"
                cy="100"
                r="80"
                stroke={accent}
                strokeOpacity="0.25"
                strokeWidth="1.5"
                strokeDasharray="2 9"
                strokeLinecap="round"
            />

            {/* Ghosted lesson cards flanking the lock — they make the graphic read
                as "content withheld" rather than a generic security padlock. Kept
                wide of centre so the lock body never buries them. */}
            <g opacity="0.95">
                <g transform="rotate(-13 46 100)">
                    <rect x="18" y="62" width="56" height="72" rx="11" fill={`url(#${g("card")})`} stroke="#e2e8f0" strokeWidth="1.5" />
                    <rect x="28" y="76" width="30" height="5" rx="2.5" fill="#cbd5e1" />
                    <rect x="28" y="88" width="38" height="5" rx="2.5" fill="#e2e8f0" />
                    <rect x="28" y="100" width="22" height="5" rx="2.5" fill="#eef2f7" />
                </g>
                <g transform="rotate(13 194 100)">
                    <rect x="166" y="62" width="56" height="72" rx="11" fill={`url(#${g("card")})`} stroke="#e2e8f0" strokeWidth="1.5" />
                    <rect x="176" y="76" width="30" height="5" rx="2.5" fill="#cbd5e1" />
                    <rect x="176" y="88" width="38" height="5" rx="2.5" fill="#e2e8f0" />
                    <rect x="176" y="100" width="22" height="5" rx="2.5" fill="#eef2f7" />
                </g>
            </g>

            {/* Padlock. The shackle is drawn twice — a wider soft stroke underneath
                gives it edge definition, otherwise the metallic gradient washes out
                against the aura. */}
            <path d="M101 86V68a19 19 0 0 1 38 0v18" stroke="#64748b" strokeOpacity="0.35" strokeWidth="15" strokeLinecap="round" />
            <path d="M101 86V68a19 19 0 0 1 38 0v18" stroke={`url(#${g("shackle")})`} strokeWidth="12" strokeLinecap="round" />
            <rect x="79" y="86" width="82" height="72" rx="20" fill={`url(#${g("body")})`} />
            {/* top highlight, so the body reads as a solid object not a flat chip */}
            <rect x="79" y="86" width="82" height="32" rx="20" fill="#ffffff" fillOpacity="0.18" />
            {/* keyhole */}
            <circle cx="120" cy="116" r="9.5" fill="#ffffff" fillOpacity="0.95" />
            <path d="M116.4 122.5h7.2l2.1 15h-11.4z" fill="#ffffff" fillOpacity="0.95" />

            {/* reassurance badge — the legacy art's shield-check, restyled */}
            <g transform="translate(156 140)">
                <circle cx="0" cy="0" r="18" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1.5" />
                <path d="M0-9.5l7.4 3.2v4.2c0 4.7-3.1 8.9-7.4 10.1-4.3-1.2-7.4-5.4-7.4-10.1v-4.2z" fill={accent} fillOpacity="0.18" />
                <path d="M-3.6 0.2l2.6 2.6 4.8-5.3" stroke={accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            {/* sparkles */}
            <path d="M70 40l2.8 7.2L80 50l-7.2 2.8L70 60l-2.8-7.2L60 50l7.2-2.8z" fill={accent} fillOpacity="0.7" />
            <path d="M186 46l1.8 4.6L192 52l-4.2 1.8L186 58l-1.8-4.2L180 52l4.2-1.4z" fill={accent} fillOpacity="0.45" />
        </svg>
    );
}

